import type { AiV2MediaType } from '../../supabase/functions/_shared/aiV2Protocol';

// Photo preparation for AI analysis (Owner task book 2026-09-04 Round 2 §R): enough resolution for
// inspection detail (long edge 1280px, JPEG 0.85 — brick courses, hairline cracks and labels stay
// legible; Owner decision 2026-09-13: 1280 instead of 1600 — the current models read the full
// resolution and 1600px costs twice the image tokens of 1280px for no gain at scan time), never the
// full-resolution original, never a thumbnail. Fail-safe: when the host cannot
// decode (jsdom, exotic format) a reasonably sized original is sent as is; oversized undecodable
// files are refused rather than uploaded blind.
export const AI_IMAGE_MAX_EDGE = 1280;
export const AI_IMAGE_QUALITY = 0.85;
// Undecodable originals are sent untouched only up to this size.
export const AI_IMAGE_PASSTHROUGH_MAX_BYTES = 5_000_000;

export interface PreparedImage {
  base64: string;
  mediaType: AiV2MediaType;
  width: number | null;
  height: number | null;
  bytes: number;
  // true when the bytes were re-encoded (downscaled) here.
  resized: boolean;
}

function supportedType(type: string): AiV2MediaType | null {
  const t = type.toLowerCase();
  if (t === 'image/jpeg' || t === 'image/jpg') return 'image/jpeg';
  if (t === 'image/png') return 'image/png';
  if (t === 'image/webp') return 'image/webp';
  return null;
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => {
        const result = typeof reader.result === 'string' ? reader.result : '';
        resolve(result.includes(',') ? result.slice(result.indexOf(',') + 1) : result);
      };
      reader.onerror = () => reject(reader.error ?? new Error('read failed'));
      reader.readAsDataURL(blob);
    } catch (raw) {
      reject(raw instanceof Error ? raw : new Error(String(raw)));
    }
  });
}

async function passthrough(blob: Blob): Promise<PreparedImage | null> {
  const mediaType = supportedType(blob.type);
  if (!mediaType || blob.size === 0 || blob.size > AI_IMAGE_PASSTHROUGH_MAX_BYTES) return null;
  return { base64: await blobToBase64(blob), mediaType, width: null, height: null, bytes: blob.size, resized: false };
}

export async function prepareImageForAi(blob: Blob, options: { maxEdge?: number; quality?: number } = {}): Promise<PreparedImage | null> {
  const maxEdge = options.maxEdge ?? AI_IMAGE_MAX_EDGE;
  const quality = options.quality ?? AI_IMAGE_QUALITY;
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return passthrough(blob);
  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(blob);
    const { width, height } = bitmap;
    const scale = Math.min(1, maxEdge / Math.max(width, height, 1));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return passthrough(blob);
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const encoded = await new Promise<Blob | null>((resolve) => {
      try {
        canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
      } catch {
        resolve(null);
      }
    });
    if (!encoded || encoded.size === 0) return passthrough(blob);
    return { base64: await blobToBase64(encoded), mediaType: 'image/jpeg', width: canvas.width, height: canvas.height, bytes: encoded.size, resized: true };
  } catch {
    return passthrough(blob);
  } finally {
    try {
      bitmap?.close();
    } catch {
      // nothing to release
    }
  }
}
